import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { ListPermissionHoldersResponse, PermissionHolder } from "@wallyo/contracts/console";
import { actionName, t } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { featureName, permissionName } from "./team-format";
import { limitLabel, staffWriteActions, throughLabel, type WhoState } from "./who-can-do-this";

export type WhoCanDoThisProps = {
  actionId: string;
  state: WhoState;
  onAction: (actionId: string) => void;
  onRetry: () => void;
};

/** Which feature gates the action, and which permission decides who may take it. */
function WhatGatesIt({ answer }: { answer: ListPermissionHoldersResponse }) {
  const { permission } = answer;
  if (!permission) {
    return (
      <div className="who-about">
        <Gate feature={answer.feature} />
        <p className="meta">{t("team.who.noPermission")}</p>
      </div>
    );
  }
  return (
    <div className="who-about">
      <Gate feature={answer.feature} />
      <p>
        <span className="meta">{t("team.who.permission")}</span> <b>{permissionName(permission.settingId)}</b>
        <Askable approvable={permission.approvable} />
      </p>
      <p className="mono meta">{permission.settingId}</p>
    </div>
  );
}

function Gate({ feature }: { feature: StaffFeatureId | null }) {
  if (!feature) return null;
  return (
    <p>
      <span className="meta">{t("team.who.feature")}</span> <b>{featureName(feature)}</b>
    </p>
  );
}

function Askable({ approvable }: { approvable: boolean }) {
  if (!approvable) return null;
  return <span className="pill info">{t("team.who.approvable")}</span>;
}

function HolderRow({ holder, limited }: { holder: PermissionHolder; limited: boolean }) {
  return (
    <tr data-holder={holder.staffMemberId}>
      <td>
        <b>{holder.name}</b>
        <AskMarker mayRequest={holder.mayRequest} />
      </td>
      <td>{throughLabel(holder)}</td>
      <td>{limitLabel(holder, limited)}</td>
    </tr>
  );
}

/** They hold the feature but not the permission: their attempt becomes a request. */
function AskMarker({ mayRequest }: { mayRequest: boolean }) {
  if (!mayRequest) return null;
  return (
    <>
      <br />
      <span className="meta" data-ask="">
        {t("team.who.mayRequest")}
      </span>
    </>
  );
}

function Holders({ answer }: { answer: ListPermissionHoldersResponse }) {
  if (answer.holders.length === 0) {
    return (
      <p className="foot" role="status">
        {t("team.who.empty")}
      </p>
    );
  }
  const limited = answer.permission?.limited === true;
  return (
    <div className="table-wrap">
      <table className="who">
        <thead>
          <tr>
            <th>{t("team.col.name")}</th>
            <th>{t("team.who.col.through")}</th>
            <th>{t("team.who.col.limit")}</th>
          </tr>
        </thead>
        <tbody>
          {answer.holders.map((holder) => (
            <HolderRow key={holder.staffMemberId} holder={holder} limited={limited} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Answer({ state, onRetry }: { state: WhoState; onRetry: () => void }) {
  if (state.status === "failed") {
    return (
      <div className="audit-error">
        <p className="foot">{t("team.who.loadFailed")}</p>
        <ErrorNotice error={state.error} />
        <div className="btns">
          <button className="btn" type="button" onClick={onRetry}>
            {t("team.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (!state.data) {
    return (
      <div className="empty" role="status">
        <span className="spinner" aria-hidden="true" />
        <span>{t("team.who.loading")}</span>
      </div>
    );
  }
  return (
    <>
      <WhatGatesIt answer={state.data} />
      <Holders answer={state.data} />
    </>
  );
}

/**
 * Who can take one staff action now (ADR-0019 §8): the feature that gates it,
 * the permission that decides it, and every active staff member who holds it,
 * those who can act alone first and those who may only ask after them.
 */
export function WhoCanDoThisView({ actionId, state, onAction, onRetry }: WhoCanDoThisProps) {
  return (
    <section className="card who-can" aria-labelledby="team-who">
      <div className="top">
        <h2 id="team-who">{t("team.tab.who")}</h2>
      </div>
      <div className="field">
        <label htmlFor="who-action">{t("team.who.action")}</label>
        <select id="who-action" value={actionId} onChange={(event) => onAction(event.target.value)}>
          {staffWriteActions().map((group) => (
            <optgroup key={group.area} label={t(`area.${group.area}`)}>
              {group.actions.map((each) => (
                <option key={each} value={each}>
                  {actionName(each)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <Answer state={state} onRetry={onRetry} />
    </section>
  );
}
